import React, { useEffect, useMemo } from 'react';
import moment from 'moment';
import type OrderItemStepTodo from '@this/src/domain/arrangement/order_item_step_todo';
import type OrderItemStepTime from '@this/src/domain/order_item/order_item_step_time';
import {
  computeOrderItemStepElapsedSeconds,
  computeOrderItemStepElapsedSecondsFromTodo,
  getOrderItemStepTimesFromTodo
} from '@this/src/domain/arrangement/compute_order_item_step_elapsed_seconds';

interface Props {
  todo: OrderItemStepTodo;
  isRunning?: boolean;
  frozenSeconds?: number;
}

const OrderItemStepDurationCounter: React.FC<Props> = ({ todo, isRunning = true, frozenSeconds }) => {
  const stepTimes = getOrderItemStepTimesFromTodo(todo);
  const { stepStatus } = todo;
  const hasElapsedSource = stepTimes.length > 0 || todo.startedAt !== undefined || todo.duration !== undefined;

  if (!hasElapsedSource) {
    return null;
  }

  const running = isRunning && stepStatus === 'started';

  return (
    <div>
      <div>
        <span>経過時間: </span>
        {running ? (
          <TimeElapsed stepTimes={stepTimes} todo={todo} />
        ) : (
          <FrozenTime seconds={frozenSeconds ?? computeOrderItemStepElapsedSecondsFromTodo(todo)} />
        )}
      </div>
    </div>
  );
};

const formatElapsedSeconds = (seconds: number): string => {
  const elapsedMinutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${elapsedMinutes}分${remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds}秒`;
};

const FrozenTime: React.FC<{ seconds: number }> = ({ seconds }) => {
  return <span>{formatElapsedSeconds(seconds)}</span>;
};

const TimeElapsed: React.FC<{ stepTimes: OrderItemStepTime[]; todo: OrderItemStepTodo }> = ({
  stepTimes,
  todo
}) => {
  const [time, setTime] = React.useState(0);
  const formattedTime = useMemo(() => formatElapsedSeconds(time), [time]);

  useEffect(() => {
    const update = () => {
      const at = moment();
      if (stepTimes.length > 0) {
        setTime(computeOrderItemStepElapsedSeconds(stepTimes, at));
        return;
      }
      if (todo.startedAt) {
        setTime(Math.floor(moment.duration(at.diff(todo.startedAt)).asSeconds()));
        return;
      }
      setTime(todo.duration ?? 0);
    };

    update();
    const interval = setInterval(update, 1000);

    return () => clearInterval(interval);
  }, [stepTimes, todo]);

  return <span>{formattedTime}</span>;
};

export default OrderItemStepDurationCounter;
